'use client';

/**
 * 议程 tree（左栏）
 *
 * - dnd-kit 拖排序：段维度 + 项维度（项不跨段；MVP 简化）
 * - 段卡片：title / 项数 / 加项 / 编辑（rename） / 删段 / 分配任务（item 操作） / 上传资料（item 操作）
 *
 * 设计选择：拖完立即调 reorder API；失败回滚本地顺序 + toast。
 */

import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  Columns,
  Edit2,
  GripVertical,
  Plus,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import {
  agendaApi,
  type MeetingAgendaItem,
  type MeetingAgendaSection,
} from '@/services/api/agenda';
import { SectionColumnsDialog } from './SectionColumnsDialog';
interface Props {
  meetingId: string;
  sections: MeetingAgendaSection[];
  selectedItemId: string | null;
  onSelectItem: (sectionId: string, item: MeetingAgendaItem) => void;
  onReload: () => Promise<void>;
  onLocalSectionsChange: (sections: MeetingAgendaSection[]) => void;
  onRequestAssign: (item: MeetingAgendaItem) => void;
  onRequestUploadFor: (item: MeetingAgendaItem) => void;
}

export function AgendaTree({
  meetingId,
  sections,
  selectedItemId,
  onSelectItem,
  onReload,
  onLocalSectionsChange,
  onRequestAssign,
  onRequestUploadFor,
}: Props) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(sections.map((s) => s.id)),
  );
  const [renameSectionId, setRenameSectionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [configureSection, setConfigureSection] = useState<MeetingAgendaSection | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ─── 段操作 ─────────────────────────────────────────────────────────────

  const toggleSection = (id: string) =>
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSectionsReorder = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(sections, oldIdx, newIdx);
    onLocalSectionsChange(next);
    try {
      await agendaApi.reorderAgendaSections(
        meetingId,
        next.map((s) => s.id),
      );
      toast.success(tag.reorderSuccess);
    } catch {
      toast.error(tag.reorderFailed);
      onLocalSectionsChange(sections); // 回滚
    }
  };

  const handleItemsReorder = async (
    sectionId: string,
    items: MeetingAgendaItem[],
    event: DragEndEvent,
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((it) => it.id === active.id);
    const newIdx = items.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(items, oldIdx, newIdx);
    onLocalSectionsChange(
      sections.map((s) => (s.id === sectionId ? { ...s, items: reordered } : s)),
    );
    try {
      await agendaApi.reorderAgendaItems(
        sectionId,
        reordered.map((it) => it.id),
      );
      toast.success(tag.reorderSuccess);
    } catch {
      toast.error(tag.reorderFailed);
      await onReload();
    }
  };

  const handleCreateSection = async () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    try {
      await agendaApi.createAgendaSection(meetingId, { title });
      setNewSectionTitle('');
      setCreatingSection(false);
      await onReload();
    } catch {
      toast.error(tag.saveFailed);
    }
  };

  const handleRenameSection = async (sectionId: string) => {
    const title = renameValue.trim();
    if (!title) {
      toast.error(tag.section.saveFailedTitleEmpty);
      return;
    }
    if (title.length > 200) {
      toast.error(tag.section.saveFailedTitleTooLong);
      return;
    }
    try {
      await agendaApi.updateAgendaSection(meetingId, sectionId, { title });
      setRenameSectionId(null);
      await onReload();
    } catch {
      toast.error(tag.saveFailed);
    }
  };

  const handleDeleteSection = async (section: MeetingAgendaSection) => {
    if (
      !window.confirm(
        `${tag.section.deleteConfirmTitle}\n\n${tag.section.deleteConfirmBody}\n\n[${section.title}]`,
      )
    ) {
      return;
    }
    try {
      await agendaApi.deleteAgendaSection(meetingId, section.id);
      await onReload();
    } catch {
      toast.error(tag.saveFailed);
    }
  };

  const handleAddItem = async (section: MeetingAgendaSection) => {
    try {
      const resp = await agendaApi.createAgendaItem(section.id, { title: tag.item.title });
      await onReload();
      // 选中新创建的 item 让用户立即编辑
      onSelectItem(section.id, resp.item);
    } catch {
      toast.error(tag.saveFailed);
    }
  };

  const handleDeleteItem = async (section: MeetingAgendaSection, item: MeetingAgendaItem) => {
    const taskCount = item.uploadTasks?.filter((t) => t.status !== 'CANCELLED').length || 0;
    const attCount = item.attachments?.length || 0;
    const msg = tag.item.deleteConfirmBody
      .replace('{n}', String(attCount))
      .replace('{m}', String(taskCount));
    if (!window.confirm(`${tag.item.deleteConfirmTitle}\n\n${msg}\n\n[${item.title}]`)) {
      return;
    }
    try {
      await agendaApi.deleteAgendaItem(section.id, item.id);
      await onReload();
    } catch {
      toast.error(tag.saveFailed);
    }
  };

  // ─── 渲染 ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {sections.length === 0 ? (
        <div className="rounded border-2 border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          {tag.emptyTree}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionsReorder}>
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  expanded={expandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                  onRename={() => {
                    setRenameSectionId(section.id);
                    setRenameValue(section.title);
                  }}
                  renaming={renameSectionId === section.id}
                  renameValue={renameValue}
                  onRenameValueChange={setRenameValue}
                  onRenameSubmit={() => handleRenameSection(section.id)}
                  onRenameCancel={() => setRenameSectionId(null)}
                  onDelete={() => handleDeleteSection(section)}
                  onConfigureColumns={() => setConfigureSection(section)}
                  onAddItem={() => handleAddItem(section)}
                  selectedItemId={selectedItemId}
                  onSelectItem={(item) => onSelectItem(section.id, item)}
                  onItemsReorder={(event) => handleItemsReorder(section.id, section.items, event)}
                  onDeleteItem={(item) => handleDeleteItem(section, item)}
                  onAssignTask={(item) => onRequestAssign(item)}
                  onUploadForItem={(item) => onRequestUploadFor(item)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* 加段输入 */}
      {creatingSection ? (
        <div className="rounded border border-gray-200 bg-gray-50 p-2">
          <Input
            autoFocus
            value={newSectionTitle}
            onChange={(e) => setNewSectionTitle(e.target.value)}
            placeholder={tag.section.titlePlaceholder}
            maxLength={200}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateSection();
              if (e.key === 'Escape') {
                setCreatingSection(false);
                setNewSectionTitle('');
              }
            }}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreatingSection(false);
                setNewSectionTitle('');
              }}
            >
              {t.meetingAttendance.common.back}
            </Button>
            <Button size="sm" onClick={handleCreateSection}>
              {tag.saveButton}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="w-full" onClick={() => setCreatingSection(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tag.section.addSection}
        </Button>
      )}

      {configureSection && (
        <SectionColumnsDialog
          meetingId={meetingId}
          section={configureSection}
          open={!!configureSection}
          onOpenChange={(o) => !o && setConfigureSection(null)}
          onSaved={() => {
            // edit 页保留 full reload（左右两栏会因 tree 变化重渲染）
            void onReload();
          }}
        />
      )}
    </div>
  );
}

// ─── 子组件：可拖段卡 ─────────────────────────────────────────────────────

interface SectionProps {
  section: MeetingAgendaSection;
  expanded: boolean;
  onToggle: () => void;
  onRename: () => void;
  renaming: boolean;
  renameValue: string;
  onRenameValueChange: (v: string) => void;
  onRenameSubmit: () => void;
  onRenameCancel: () => void;
  onDelete: () => void;
  onConfigureColumns: () => void;
  onAddItem: () => void;
  selectedItemId: string | null;
  onSelectItem: (item: MeetingAgendaItem) => void;
  onItemsReorder: (event: DragEndEvent) => void;
  onDeleteItem: (item: MeetingAgendaItem) => void;
  onAssignTask: (item: MeetingAgendaItem) => void;
  onUploadForItem: (item: MeetingAgendaItem) => void;
}

function SortableSection({
  section,
  expanded,
  onToggle,
  onRename,
  renaming,
  renameValue,
  onRenameValueChange,
  onRenameSubmit,
  onRenameCancel,
  onDelete,
  onConfigureColumns,
  onAddItem,
  selectedItemId,
  onSelectItem,
  onItemsReorder,
  onDeleteItem,
  onAssignTask,
  onUploadForItem,
}: SectionProps) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  return (
    <div ref={setNodeRef} style={style} className="rounded border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b bg-gray-50 px-2 py-2">
        <button
          type="button"
          className="cursor-grab text-gray-400 hover:text-gray-600"
          aria-label={tag.section.reorderHint}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button type="button" onClick={onToggle}>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </button>
        {renaming ? (
          <Input
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRenameSubmit();
              if (e.key === 'Escape') onRenameCancel();
            }}
            onBlur={onRenameSubmit}
            maxLength={200}
            className="h-7 text-sm"
          />
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-gray-900">{section.title}</span>
            <span className="text-xs text-gray-500">
              {tag.section.itemsCount.replace('{n}', String(section.items.length))}
            </span>
          </>
        )}
        <div className="flex items-center gap-1">
          {section.columnLabels.length > 0 && (
            <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] text-purple-700 border border-purple-200">
              {tag.section.columnsBadge.replace('{n}', String(section.columnLabels.length))}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onConfigureColumns}
            title={tag.section.configureColumns}
          >
            <Columns className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onRename} title={tag.section.editSection}>
            <Edit2 className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} title={tag.section.deleteSection}>
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-1 p-2">
          {section.items.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onItemsReorder}
            >
              <SortableContext
                items={section.items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                {section.items.map((it) => (
                  <SortableItem
                    key={it.id}
                    item={it}
                    selected={selectedItemId === it.id}
                    onSelect={() => onSelectItem(it)}
                    onDelete={() => onDeleteItem(it)}
                    onAssign={() => onAssignTask(it)}
                    onUpload={() => onUploadForItem(it)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          <Button variant="ghost" size="sm" className="w-full" onClick={onAddItem}>
            <Plus className="mr-1 h-3 w-3" />
            {tag.item.addItem}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── 子组件：可拖项卡 ────────────────────────────────────────────────────

interface ItemProps {
  item: MeetingAgendaItem;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onAssign: () => void;
  onUpload: () => void;
}

function SortableItem({ item, selected, onSelect, onDelete, onAssign, onUpload }: ItemProps) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const pendingCount = item.uploadTasks?.filter((t) => t.status === 'PENDING').length || 0;
  const uploadedCount = item.uploadTasks?.filter((t) => t.status === 'UPLOADED').length || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded border ${
        selected ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 bg-white'
      } px-2 py-1.5`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-gray-300 hover:text-gray-500"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={onSelect}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm text-gray-900">{item.title}</span>
            {item.code && (
              <span className="text-[10px] text-gray-500 font-mono">{item.code}</span>
            )}
            {typeof item.timeMinutes === 'number' && item.timeMinutes > 0 && (
              <span className="text-[10px] text-gray-500">{item.timeMinutes}m</span>
            )}
            {item.presenter && (
              <span className="text-[10px] text-gray-500">@{item.presenter.displayName}</span>
            )}
            {pendingCount > 0 && (
              <span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] text-amber-800">
                {tag.uploadTask.statusBadgePending.replace('{n}', String(pendingCount))}
              </span>
            )}
            {uploadedCount > 0 && (
              <span className="rounded bg-green-100 px-1 py-0.5 text-[10px] text-green-800">
                {tag.uploadTask.statusBadgeUploaded.replace('{n}', String(uploadedCount))}
              </span>
            )}
          </div>
        </button>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
          <Button variant="ghost" size="sm" onClick={onAssign} title={tag.item.assignTask}>
            <UserPlus className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onUpload} title={tag.item.uploadFile}>
            <Upload className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} title={tag.item.deleteItem}>
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}
